import {
  NOTIFICATION_DURATIONS,
  NOTIFICATION_POSITIONS,
} from "@/constants/NotificationConstants";
import { NotificationGlobalContextType } from "@/types/NotificationTypes";
import React, { useCallback, useEffect, useState } from "react";

const Toast: React.FC<NotificationGlobalContextType> = ({
  type,
  message,
  isVisible,
  onClose,
  duration = NOTIFICATION_DURATIONS.MEDIUM,
  position = NOTIFICATION_POSITIONS.BOTTOM_RIGHT,
  className = "",
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);

      if (duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);

        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, duration, handleClose]);

  if (!isVisible) return null;

  // Toast type configurations
  const toastConfig = {
    success: {
      borderColor: "border-app-green",
      textColor: "text-app-green",
      bgColor: "bg-white",
      icon: (
        <svg className="w-6 h-6 mr-2.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    error: {
      borderColor: "border-app-red",
      textColor: "text-app-red",
      bgColor: "bg-white",
      icon: (
        <svg className="w-6 h-6 mr-2.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    warning: {
      borderColor: "border-yellow-500",
      textColor: "text-yellow-600",
      bgColor: "bg-white",
      icon: (
        <svg className="w-6 h-6 mr-2.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    info: {
      borderColor: "border-blue-500",
      textColor: "text-blue-600",
      bgColor: "bg-white",
      icon: (
        <svg className="w-6 h-6 mr-2.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  };
  const positionClasses = {
    [NOTIFICATION_POSITIONS.TOP_RIGHT]: "top-4 right-4",
    [NOTIFICATION_POSITIONS.TOP_LEFT]: "top-4 left-4",
    [NOTIFICATION_POSITIONS.BOTTOM_RIGHT]: "bottom-4 right-4",
    [NOTIFICATION_POSITIONS.BOTTOM_LEFT]: "bottom-4 left-4",
    [NOTIFICATION_POSITIONS.TOP_CENTER]:
      "top-4 left-1/2 transform -translate-x-1/2",
    [NOTIFICATION_POSITIONS.BOTTOM_CENTER]:
      "bottom-4 left-1/2 transform -translate-x-1/2",
  };

  const config = toastConfig[type as keyof typeof toastConfig];

  return (
    <div
      className={`
        fixed z-[999999999] px-4 py-3 rounded-md flex items-center justify-between
        shadow-lg min-w-[300px] max-w-[500px]
        ${config.bgColor} ${config.borderColor} ${config.textColor}
        border transition-all duration-300 ease-in-out
        ${positionClasses[position as keyof typeof positionClasses]}
        ${
          isAnimating
            ? "block transform translate-y-0 scale-100"
            : "hidden transform translate-y-2 scale-95"
        }
        ${className}
      `}
    >
      <div className="flex items-center flex-1 mr-4">
        {config.icon}
        <span className="font-normal text-base leading-6 tracking-normal text-dark-text">
          {message}
        </span>
      </div>
      <button
        onClick={handleClose}
        className="text-gray-600 hover:text-dark-text text-base cursor-pointer flex-shrink-0 w-6 h-6 -mt-3 flex items-center justify-center"
      >
        x
      </button>
    </div>
  );
};

export default Toast;
